import { api } from '@/lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface RecipeIngredient {
    id: string;
    amount: number;
    unit: string;
    preparation?: string;
    isOptional?: boolean;
    ingredient: {
        id: string;
        name: string;
    };
}

export interface RecipeCategory {
    id: string;
    name: string;
    color?: string;
    isPositive?: boolean;
    pointValue?: number;
}

// Backend returns nutrition categories in this nested structure
export interface RecipeNutritionCategory {
    id: string;
    servings: number;
    category: {
        id: string;
        name: string;
        points: number;
    };
}

export interface RecipeCategoryFilter {
    id: string;
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    _count: {
        recipes: number;
    };
}

// Nutrition/points categories (the coach-tagged Category system) used for filtering
export interface NutritionCategoryFilter {
    id: string;
    name: string;
    points: number;
    displayOrder: number;
}

export interface Recipe {
    id: string;
    name: string;
    description?: string;
    imageUrl?: string;
    prepTime?: number;
    cookTime?: number;
    servings?: number;
    instructions?: string;
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    isPublic?: boolean;
    isApproved?: boolean;
    createdAt: string;
    ingredients?: RecipeIngredient[];
    nutritionCategories?: RecipeNutritionCategory[];
    categories?: Array<{ id: string; name: string }>;
    dietaryTags?: Array<{ id: string; name: string }>;
    cuisineType?: string;
    difficulty?: string;
    averageRating?: number;
    totalRatings?: number;
    // User-specific data (only present when authenticated)
    saves?: Array<{ id: string }>;
    ratings?: Array<{ rating: number; review?: string; createdAt: string }>;
}

export interface RecipeQueryParams {
    search?: string;
    page?: number;
    limit?: number;
    categories?: string[];
    nutritionCategories?: string[];
    dietaryTags?: string[];
    cuisineType?: string;
    difficulty?: string;
    maxCalories?: number;
    maxPrepTime?: number;
    sortBy?: 'created' | 'name' | 'rating' | 'prepTime' | 'calories';
    sortOrder?: 'asc' | 'desc';
}

export interface RecipePagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

export interface RecipeListResponse {
    recipes: Recipe[];
    pagination: RecipePagination;
}

// API functions
export const getRecipes = async (params: RecipeQueryParams = {}): Promise<RecipeListResponse> => {
    // Repeat array keys (?categories=a&categories=b); the backend's Express 5
    // query parser does not understand axios' default categories[]=a form.
    const response = await api.get('/recipes', {
        params,
        paramsSerializer: { indexes: null },
    });
    return response.data;
};

export const getRecipeById = async (id: string): Promise<Recipe> => {
    const response = await api.get(`/recipes/${id}`);
    return response.data;
};

export const saveRecipe = async (
    recipeId: string,
): Promise<{ saved: boolean; message: string }> => {
    const response = await api.post(`/recipes/${recipeId}/save`);
    return response.data;
};

export const getSavedRecipes = async (page = 1, limit = 20): Promise<RecipeListResponse> => {
    const response = await api.get('/recipes/saved/list', { params: { page, limit } });
    return response.data;
};

// React Query hooks
export const useRecipes = (params: RecipeQueryParams = {}) => {
    return useQuery({
        queryKey: ['recipes', params],
        queryFn: () => getRecipes(params),
    });
};

export const useRecipe = (id: string) => {
    return useQuery({
        queryKey: ['recipe', id],
        queryFn: () => getRecipeById(id),
        enabled: !!id,
    });
};

export const useSavedRecipes = (page = 1, limit = 20) => {
    return useQuery({
        queryKey: ['saved-recipes', page, limit],
        queryFn: () => getSavedRecipes(page, limit),
    });
};

// Get all saved recipe IDs for checking save status on listings
export const useSavedRecipeIds = () => {
    return useQuery({
        queryKey: ['saved-recipe-ids'],
        queryFn: async () => {
            // Fetch a large batch to get all saved recipes
            const result = await getSavedRecipes(1, 1000);
            return new Set(result.recipes.map((r) => r.id));
        },
        staleTime: 30 * 1000, // Cache for 30 seconds
    });
};

export const useSaveRecipe = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: saveRecipe,
        onSuccess: (_data, recipeId) => {
            // Invalidate saved recipes list, IDs cache, and the specific recipe
            queryClient.invalidateQueries({ queryKey: ['saved-recipes'] });
            queryClient.invalidateQueries({ queryKey: ['saved-recipe-ids'] });
            queryClient.invalidateQueries({ queryKey: ['recipe', recipeId] });
        },
    });
};

// Rating
export const rateRecipe = async (
    recipeId: string,
    data: { rating: number; review?: string },
): Promise<{ averageRating: number; totalRatings: number; userRating: number }> => {
    const response = await api.post(`/recipes/${recipeId}/rate`, data);
    return response.data;
};

export const useRateRecipe = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({
            recipeId,
            data,
        }: {
            recipeId: string;
            data: { rating: number; review?: string };
        }) => rateRecipe(recipeId, data),
        onSuccess: (_data, variables) => {
            // Invalidate the specific recipe to refresh rating
            queryClient.invalidateQueries({ queryKey: ['recipe', variables.recipeId] });
        },
    });
};

// Recipe categories (meal types)
export const getRecipeCategories = async (): Promise<RecipeCategoryFilter[]> => {
    const response = await api.get('/recipes/categories');
    return response.data;
};

export const useRecipeCategories = () => {
    return useQuery({
        queryKey: ['recipe-categories'],
        queryFn: getRecipeCategories,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });
};

// Nutrition/points categories (public GET /categories returns active, displayOrder-sorted)
export const getNutritionCategories = async (): Promise<NutritionCategoryFilter[]> => {
    const response = await api.get('/categories');
    return response.data;
};

export const useNutritionCategories = () => {
    return useQuery({
        queryKey: ['nutrition-categories'],
        queryFn: getNutritionCategories,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });
};
